// Frontend mirror of backend permissions (permissions.py) for UI gating only.
// The server remains the source of truth and enforces all access on /assistant/chat.

export function planOf(user) {
  return user?.subscription?.plan || "service";
}

export function roleOf(user) {
  return user?.role || "user";
}

// Mirror backend permissions.sub_active: active, trialing (7-day Strategist trial)
// and past_due (Stripe retry grace) all keep access. Only terminal states lock.
export const ACTIVE_STATUSES = ["active", "trialing", "past_due"];

export function subActive(user) {
  return ACTIVE_STATUSES.includes(user?.subscription?.status || "active");
}

export function isFounder(user) {
  return roleOf(user) === "founder";
}

export function isAdmin(user) {
  return ["admin", "founder"].includes(roleOf(user));
}

// Employee roles get unlimited AI (Founder, Admin, Consultant). Mirrors
// permissions.EMPLOYEE_ROLES on the backend.
export const EMPLOYEE_ROLES = ["founder", "admin", "consultant"];

export function isUnlimited(user) {
  // Employee roles only. The Strategist PLAN now has a 1000/mo cap (not unlimited).
  return EMPLOYEE_ROLES.includes(roleOf(user));
}

// AI modes -> which shared monthly credit pool they consume.
// "assistant" = Standard pool (50/mo): Supporter, Calendar Assistant, Motivator.
// "strategist" = Advanced pool (10/mo): Statistics, Strategist/Advisor.
export const MODE_LEVEL = {
  // Consolidated roles
  assistant: "assistant",
  strategy: "strategist",
  support: "assistant",
  // Legacy roles (backward compatibility)
  reflection: "assistant",
  calendar: "assistant",
  motivator: "assistant",
  statistics: "strategist",
  advisor: "strategist",
};

// Standard (50) and Advanced (10) monthly pool sizes for the Assistant plan.
export const POOL_MAX = { assistant: 50, strategist: 10 };
// Strategist PLAN: one shared monthly pool of 1000 AI credits.
export const STRATEGIST_PLAN_POOL = 1000;

// Convenience accessors for the two shared credit pools.
export function standardCredits(user) {
  return user?.subscription?.assistantCreditsRemaining ?? 0;
}
export function advancedCredits(user) {
  return user?.subscription?.strategistCreditsRemaining ?? 0;
}

// Remaining AI credits for the Strategist plan's single 1000 pool (legacy
// "unlimited"/missing values are treated as a full pool).
export function strategistPlanCredits(user) {
  const v = user?.subscription?.strategistCreditsRemaining;
  return typeof v === "number" ? v : STRATEGIST_PLAN_POOL;
}

// Whether the user has access to AI at all (Service plan => fully locked).
export function aiUnlocked(user) {
  if (isUnlimited(user)) return true;
  return ["assistant", "strategist"].includes(planOf(user)) && subActive(user);
}

// Access for a specific AI level ("assistant" | "strategist").
export function aiAccess(user, level) {
  if (isUnlimited(user)) {
    return { allowed: true, unlimited: true, credits: "unlimited", reason: null, max: null };
  }
  if (planOf(user) === "assistant" && subActive(user)) {
    const field =
      level === "assistant" ? "assistantCreditsRemaining" : "strategistCreditsRemaining";
    const remaining = user?.subscription?.[field] ?? 0;
    if (remaining === "unlimited") {
      return { allowed: true, unlimited: true, credits: "unlimited", reason: null, max: null };
    }
    const allowed = typeof remaining === "number" && remaining > 0;
    return {
      allowed,
      unlimited: false,
      credits: remaining,
      reason: allowed ? null : "no_credits",
      max: level === "assistant" ? POOL_MAX.assistant : POOL_MAX.strategist,
    };
  }
  if (planOf(user) === "strategist" && subActive(user)) {
    const remaining = strategistPlanCredits(user);
    const allowed = remaining > 0;
    return {
      allowed,
      unlimited: false,
      credits: remaining,
      reason: allowed ? null : "no_credits",
      max: STRATEGIST_PLAN_POOL,
    };
  }
  return { allowed: false, unlimited: false, credits: 0, reason: "upgrade_required", max: null };
}
